/**
 * The trips. Each one is a drive, a boat ride or a train through one part of
 * the state in one year, stop by stop.
 *
 * WHAT A STOP IS. The player puts a pin where they think the place is, which
 * is scored by distance and always pays something. Then one question, in
 * their own words rather than a quiz voice, and then a postcard: the thing
 * worth knowing or remembering about the place. The postcard is the point.
 * The question is there so the postcard has something to land on.
 *
 * WHAT A QUESTION MAY NOT BE. Never a trick, never a date to the year unless
 * the year is the story, never something only a local historian would know
 * without the question itself giving a fair run at it. The first option is
 * the right one in the source and the client shuffles them, so the order
 * here means nothing.
 *
 * NO STOP ON A LABELLED CITY. The anchors in `places.ts` carry a label on the
 * map, and a labelled answer is not a question. A test holds this.
 */

import type { Trip } from './types';

export const TRIPS: readonly Trip[] = [
  // -------------------------------------------------------------------------
  {
    id: 'route-9-north-1948',
    title: 'Up Route 9 to the North Country',
    era: 'auto',
    year: 1948,
    region: 'northroad',
    intro:
      'It is the summer of 1948. There is no Northway and there will not be one for twenty years. From Albany the road north is Route 9, two lanes through every village, with a stop at every light.',
    stops: [
      {
        place: 'saratoga',
        question: {
          kind: 'choice',
          text: 'What put Saratoga Springs on the map, long before the racetrack?',
          options: ['The mineral springs', 'A canal junction', 'An iron mine', 'A paper mill'],
          answer: 0,
          explain:
            'People came to take the waters. The racetrack followed in 1863 and is the oldest thoroughbred track still running in the country.',
        },
        postcard:
          'Potato chips were first fried up at Moon\'s Lake House on Saratoga Lake in the 1850s and sold as Saratoga chips.',
      },
      {
        place: 'glensfalls',
        question: {
          kind: 'truefalse',
          text: 'Look magazine once called Glens Falls "Hometown, U.S.A."',
          answer: true,
          explain: 'It did, in 1944, picking the city as a typical American town for a series of photographs.',
        },
        postcard:
          'Cooper\'s Cave, under the bridge at the falls, is where Hawkeye hid in The Last of the Mohicans.',
      },
      {
        place: 'lakegeorge',
        question: {
          kind: 'estimate',
          text: 'How long is Lake George, end to end?',
          unit: 'miles',
          min: 10,
          max: 60,
          step: 1,
          answer: 32,
          tolerance: 5,
          explain: 'About 32 miles from the village to Ticonderoga, and never more than about three miles wide.',
        },
        postcard:
          'Steamboats have run on the lake since 1817. Fort William Henry, at the south end, fell to the French in 1757.',
      },
      {
        place: 'warrensburg',
        question: {
          kind: 'choice',
          text: 'Warrensburg sits where two rivers meet. Which two?',
          options: ['The Hudson and the Schroon', 'The Hudson and the Sacandaga', 'The Hudson and the Mohawk', 'The Schroon and the Boquet'],
          answer: 0,
          explain: 'The Schroon comes down from the north and joins the Hudson just below the village.',
        },
        postcard:
          'Since 1979 the World\'s Largest Garage Sale has filled the streets here on the first weekend of October.',
      },
      {
        place: 'pottersville',
        question: {
          kind: 'choice',
          text: 'What has drawn travellers off Route 9 at Pottersville for generations?',
          options: ['Natural Stone Bridge and Caves', 'A ski jump', 'A racetrack', 'A lighthouse'],
          answer: 0,
          explain: 'Trout Brook runs under a great marble arch there, with caves and potholes worn into the rock.',
        },
        postcard: 'Pottersville is the south end of Schroon Lake, and the boat launch is just up the road.',
      },
      {
        place: 'schroonlake',
        question: {
          kind: 'truefalse',
          text: 'In 1948 you could drive from Albany to Schroon Lake on a four-lane highway.',
          answer: false,
          explain: 'The Northway did not reach this far until the 1960s. Route 9, through every village, was the only way.',
        },
        postcard:
          'The lake is about nine miles long. The summer camps and the old lakeside hotels filled up every July.',
      },
      {
        place: 'elizabethtown',
        question: {
          kind: 'choice',
          text: 'Elizabethtown is the county seat of which county?',
          options: ['Essex', 'Clinton', 'Warren', 'Hamilton'],
          answer: 0,
          explain: 'Essex County, which holds most of the High Peaks and a long stretch of the Champlain shore.',
        },
        postcard: 'The Adirondack History Museum is in the old school building on Court Street.',
      },
      {
        place: 'keeseville',
        question: {
          kind: 'choice',
          text: 'What natural wonder just outside Keeseville has been taking in tourists since 1870?',
          options: ['Ausable Chasm', 'Howe Caverns', 'Watkins Glen', 'Taughannock Falls'],
          answer: 0,
          explain: 'The sandstone gorge of the Ausable River, which people have called the Grand Canyon of the East.',
        },
        postcard:
          'From here Route 9 runs on to Plattsburgh and the border. In 1948 that was still an all-day drive from Albany.',
      },
    ],
  },

  // -------------------------------------------------------------------------
  {
    id: 'route-28-mountains-1962',
    title: 'Route 28 through the mountains',
    era: 'thruway',
    year: 1962,
    region: 'adirondacks',
    intro:
      'It is 1962. The Thruway has been open for eight years, but the moment you turn north at Utica you are back on two lanes. Route 28 climbs from the Mohawk Valley into the central Adirondacks, lake by lake.',
    stops: [
      {
        place: 'remsen',
        question: {
          kind: 'truefalse',
          text: 'Remsen is where Route 28 leaves the Mohawk Valley and the old Adirondack railroad leaves the main line.',
          answer: true,
          explain: 'Both turn north here. The rails ran on to Thendara, Tupper Lake and Lake Placid.',
        },
        postcard: 'Baron von Steuben, who drilled Washington\'s army, is buried just up the road in Steuben.',
      },
      {
        place: 'oldforge',
        question: {
          kind: 'choice',
          text: 'Old Forge sits at the foot of which chain of lakes?',
          options: ['The Fulton Chain', 'The Saranac Lakes', 'The Eckford Chain', 'The Finger Lakes'],
          answer: 0,
          explain: 'Eight lakes in a row, First Lake to Eighth Lake, with Old Forge at the bottom of the chain.',
        },
        postcard:
          'Enchanted Forest opened here in 1956. And when the winter forecast names the coldest spot in the state, it is usually Old Forge.',
      },
      {
        place: 'inlet',
        question: {
          kind: 'choice',
          text: 'Inlet takes its name from',
          options: ['The inlet at the head of Fourth Lake', 'A railroad siding', 'A family name', 'A hotel'],
          answer: 0,
          explain: 'The hamlet sits where the water comes into Fourth Lake, the biggest of the Fulton Chain.',
        },
        postcard: 'The Fulton Chain steamboats once stopped at the dock here on their way up the lakes.',
      },
      {
        place: 'raquettelake',
        question: {
          kind: 'choice',
          text: 'Who built Camp Pine Knot on Raquette Lake in the 1870s and started the fashion for Great Camps?',
          options: ['William West Durant', 'Thomas Edison', 'J. P. Morgan', 'Melvil Dewey'],
          answer: 0,
          explain: 'Durant\'s log walls and birch bark set the style every other Great Camp copied.',
        },
        postcard: 'Durant\'s later camp, Sagamore, went to the Vanderbilts. You can still stay there.',
      },
      {
        place: 'bluemountainlake',
        question: {
          kind: 'choice',
          text: 'What opened at Blue Mountain Lake in 1957?',
          options: ['The Adirondack Museum', 'Gore Mountain ski area', 'The Olympic bobsled run', 'The Whiteface highway'],
          answer: 0,
          explain: 'It is called the Adirondack Experience now, and it is still on the hill above the lake.',
        },
        postcard:
          'The Prospect House hotel here, opened in 1882, is said to have been the first hotel anywhere with an electric light in every room.',
      },
      {
        place: 'indianlake',
        question: {
          kind: 'estimate',
          text: 'Roughly how long is Indian Lake?',
          unit: 'miles',
          min: 5,
          max: 30,
          step: 1,
          answer: 14,
          tolerance: 3,
          explain: 'About 14 miles. A dam at the outlet in 1898 raised it to hold water for the Hudson log drives.',
        },
        postcard: 'From here Route 30 runs south to Speculator and north to Long Lake.',
      },
      {
        place: 'northcreek',
        question: {
          kind: 'choice',
          text: 'What happened at the North Creek railroad station in September 1901?',
          options: [
            'Theodore Roosevelt learned he was president',
            'The first ski train arrived',
            'The Adirondack branch opened',
            'A gold strike was announced',
          ],
          answer: 0,
          explain:
            'Roosevelt was at Tahawus when McKinley took a turn for the worse. He rode down by buckboard through the night, and at North Creek he was handed the telegram saying McKinley had died.',
        },
        postcard:
          'The first ski trains from Schenectady pulled in here in 1934. Gore Mountain opened as a state ski area in 1964.',
      },
    ],
  },

  // -------------------------------------------------------------------------
  {
    id: 'sacandaga-1929',
    title: 'The valley under the lake',
    era: 'auto',
    year: 1929,
    region: 'sacandaga',
    intro:
      'It is 1929, and the dam at Conklingville is almost finished. Next spring the Sacandaga River will back up over nearly thirty miles of farms, hamlets and the summer park. This is the valley as it was, one last time.',
    stops: [
      {
        place: 'northville',
        question: {
          kind: 'choice',
          text: 'How did most summer visitors reach Northville in the 1920s?',
          options: [
            'The FJ and G railroad from Gloversville',
            'A steamboat up the Sacandaga',
            'The Northway',
            'A stagecoach from Saratoga',
          ],
          answer: 0,
          explain:
            'The Fonda, Johnstown and Gloversville ran to Northville from 1875. The line was cut when the valley flooded.',
        },
        postcard: 'Sacandaga Park next door was called the Coney Island of the North.',
      },
      {
        place: 'sacandagapark',
        question: {
          kind: 'choice',
          text: 'Sacandaga Park was known as',
          options: ['The Coney Island of the North', 'The Gateway to the Adirondacks', 'The Saratoga of the mountains', 'The Newport of New York'],
          answer: 0,
          explain: 'Trains from Gloversville brought thousands each summer to its hotels, its theatre and its midway.',
        },
        postcard: 'Much of the park stayed above the new waterline. The midway did not.',
      },
      {
        place: 'fishhouse',
        question: {
          kind: 'choice',
          text: 'Fish House got its name from',
          options: ['Sir William Johnson\'s fishing lodge', 'A cannery', 'A fish hatchery', 'The shape of its church'],
          answer: 0,
          explain: 'Johnson, the king\'s man among the Mohawk, kept a summer place here on the Sacandaga in the 1760s.',
        },
        postcard: 'The hamlet stayed dry. Its bridge over the river did not.',
      },
      {
        place: 'osbornbridge',
        clue: 'A hamlet on the Sacandaga just below Northville, gone under the lake in 1930.',
        question: {
          kind: 'truefalse',
          text: 'Osborn Bridge is still on the map today.',
          answer: false,
          explain: 'It went under the Great Sacandaga Lake in 1930, along with the farms around it.',
        },
        postcard:
          'Everything in the valley that could be moved was moved. The rest was burned before the water came, and the graves in the valley cemeteries were reburied on higher ground.',
      },
      {
        place: 'batchellerville',
        question: {
          kind: 'choice',
          text: 'What is Batchellerville best known for today?',
          options: ['The long bridge across the lake', 'A paper mill', 'A ski hill', 'A lighthouse'],
          answer: 0,
          explain: 'The bridge from Batchellerville across the lake has been rebuilt more than once; the current one opened in the 2010s.',
        },
        postcard: 'In 1929 there was no lake to bridge. The road simply crossed the river.',
      },
      {
        place: 'conklingville',
        question: {
          kind: 'estimate',
          text: 'The dam at Conklingville made a lake about how many miles long?',
          unit: 'miles',
          min: 10,
          max: 50,
          step: 1,
          answer: 29,
          tolerance: 5,
          explain: 'About 29 miles, from the dam back to Northville. It was built to hold back the spring floods that used to swamp Albany and Troy.',
        },
        postcard: 'The dam was finished in 1930 and the lake filled that same year.',
      },
      {
        place: 'speculator',
        question: {
          kind: 'choice',
          text: 'Crowds came to Speculator in the 1920s and 30s to watch',
          options: ['Boxers in training', 'Horse races', 'Ski jumping', 'Motorboat races'],
          answer: 0,
          explain: 'Gene Tunney trained here for his 1926 fight with Jack Dempsey, and Max Schmeling trained here later.',
        },
        postcard: 'Route 30 north from Speculator runs on to Indian Lake and, eventually, the St. Lawrence.',
      },
    ],
  },

  // -------------------------------------------------------------------------
  {
    id: 'tahawus-high-peaks-1948',
    title: 'Tahawus and the High Peaks',
    era: 'auto',
    year: 1948,
    region: 'adirondacks',
    intro:
      'It is 1948. The mine at Tahawus is working around the clock, the village has a school and a store, and the trail up Marcy starts at the end of the mine road. The High Peaks are still mostly for guides and the people they take up.',
    stops: [
      {
        place: 'newcomb',
        question: {
          kind: 'choice',
          text: 'Newcomb is the nearest town to',
          options: ['The headwaters of the Hudson', 'Lake Champlain', 'Whiteface Mountain', 'The Erie Canal'],
          answer: 0,
          explain: 'The Hudson rises on the flank of Marcy, and the young river runs past Newcomb on its way south.',
        },
        postcard:
          'In 1963 dozens of houses from Tahawus were hauled down the road to Winebrook Hills in Newcomb. Families still live in them.',
      },
      {
        place: 'tahawus',
        clue: 'The mining village that National Lead cleared out in 1963, whose houses ended up in Newcomb.',
        question: {
          kind: 'choice',
          text: 'What was dug at Tahawus in the 1940s and 50s?',
          options: ['Titanium ore', 'Garnet', 'Graphite', 'Silver'],
          answer: 0,
          explain:
            'The iron ore that defeated the ironmasters of the 1800s was full of titanium, and by the Second World War titanium was what the country wanted.',
        },
        postcard: 'The rail spur built to Tahawus in 1944 was the last new railroad laid in the Adirondacks.',
      },
      {
        place: 'upperworks',
        question: {
          kind: 'truefalse',
          text: 'The stone blast furnace at the Upper Works still stands.',
          answer: true,
          explain: 'The 1854 furnace stands beside the road to the trailhead, taller than the trees around it.',
        },
        postcard:
          'Theodore Roosevelt was at the Tahawus Club here in September 1901 when a guide came up the trail with word that McKinley was dying.',
      },
      {
        place: 'laketear',
        clue: 'A small pond high on the shoulder of Mount Marcy, the highest source of the Hudson.',
        question: {
          kind: 'choice',
          text: 'Who gave Lake Tear of the Clouds its name?',
          options: ['Verplanck Colvin, the state surveyor', 'Theodore Roosevelt', 'Ebenezer Emmons', 'Old Mountain Phelps'],
          answer: 0,
          explain: 'Colvin reached it in 1872 during his survey of the mountains and traced the Hudson to it.',
        },
        postcard: 'It sits at about 4,300 feet. Every drop of the Hudson at Albany started somewhere like this.',
      },
      {
        place: 'marcy',
        question: {
          kind: 'estimate',
          text: 'How tall is Mount Marcy?',
          unit: 'feet',
          min: 4000,
          max: 6000,
          step: 10,
          answer: 5344,
          tolerance: 100,
          explain: '5,344 feet, the highest point in the state. It was named for Governor William Marcy and first climbed in 1837.',
        },
        postcard: 'Tahawus, which the writers said meant cloud-splitter, was the name they gave the mountain.',
      },
      {
        place: 'keenevalley',
        question: {
          kind: 'choice',
          text: 'Old Mountain Phelps, the guide who cut the first trail up Marcy from this side, lived in',
          options: ['Keene Valley', 'Lake Placid', 'Newcomb', 'Saranac Lake'],
          answer: 0,
          explain: 'Orson Schofield Phelps cut his trail in 1861 and guided from Keene Valley for the rest of his life.',
        },
        postcard: 'The Ausable Club\'s land at St. Huberts is the way in to the Great Range.',
      },
      {
        place: 'heartlake',
        clue: 'The lake at the trailhead where the Van Hoevenberg trail up Marcy begins.',
        question: {
          kind: 'choice',
          text: 'The Adirondak Loj at Heart Lake is spelled that odd way because',
          options: [
            'Melvil Dewey liked simplified spelling',
            'It is the Dutch spelling',
            'A sign painter made a mistake',
            'It was named after a Mr. Loj',
          ],
          answer: 0,
          explain: 'Dewey, of the library decimal system, ran the Lake Placid Club and spelled things his own way.',
        },
        postcard: 'Henry Van Hoevenberg built the first lodge here in 1880. It burned in the great fire of 1903.',
      },
      {
        place: 'whiteface',
        question: {
          kind: 'choice',
          text: 'What did Franklin Roosevelt come to Whiteface to open in 1935?',
          options: ['The memorial highway to the summit', 'The ski area', 'The Olympic ski jumps', 'A fire tower'],
          answer: 0,
          explain: 'The toll road takes you nearly to the top. The ski area came in 1958.',
        },
        postcard: 'Whiteface is the fifth highest of the 46 High Peaks and the only one with a road up it.',
      },
    ],
  },

  // -------------------------------------------------------------------------
  {
    id: 'route-20-across-1938',
    title: 'Across the state on Route 20',
    era: 'auto',
    year: 1938,
    region: 'across',
    intro:
      'It is 1938 and you are driving from Albany to Buffalo. Route 20 rides the hills south of the Mohawk, through the villages of the old turnpike, and it is a two-day trip if you stop to eat.',
    stops: [
      {
        place: 'sharonsprings',
        question: {
          kind: 'choice',
          text: 'What brought summer crowds to Sharon Springs a century ago?',
          options: ['Sulphur springs and bathhouses', 'A racetrack', 'A canal', 'A glass factory'],
          answer: 0,
          explain: 'The grand hotels filled each summer with people taking the sulphur waters.',
        },
        postcard: 'The bathhouses are still there along the main street, and a few have been brought back to life.',
      },
      {
        place: 'cherryvalley',
        question: {
          kind: 'choice',
          text: 'The road from Albany through Cherry Valley followed which old toll road?',
          options: ['The Cherry Valley Turnpike', 'The Mohawk Turnpike', 'The Seneca Turnpike', 'The Catskill Turnpike'],
          answer: 0,
          explain: 'The turnpike ran west from Albany along the ridge, and Route 20 was laid over it.',
        },
        postcard: 'The settlement was burned in a raid in November 1778 and rebuilt after the war.',
      },
      {
        place: 'cazenovia',
        question: {
          kind: 'truefalse',
          text: 'Cazenovia sits at the foot of a lake.',
          answer: true,
          explain: 'Cazenovia Lake runs north from the village, four miles long.',
        },
        postcard: 'Lorenzo, the Lincklaen mansion of 1807, looks straight down the lake from the south shore.',
      },
      {
        place: 'skaneateles',
        question: {
          kind: 'choice',
          text: 'How do the locals say Skaneateles?',
          options: ['skinny-AT-lus', 'skan-ee-AY-teels', 'SKAN-uh-tells', 'ska-NEE-tuh-lees'],
          answer: 0,
          explain: 'Skinny-atlas. Anyone who says it another way is from somewhere else.',
        },
        postcard: 'The lake is one of the clearest in the state, and Syracuse drinks from it unfiltered.',
      },
      {
        place: 'geneva',
        question: {
          kind: 'choice',
          text: 'Geneva sits at the head of which Finger Lake?',
          options: ['Seneca', 'Cayuga', 'Keuka', 'Canandaigua'],
          answer: 0,
          explain: 'Seneca, the deepest of the Finger Lakes and the one that almost never freezes over.',
        },
        postcard: 'The state agricultural experiment station has been here since 1880.',
      },
      {
        place: 'canandaigua',
        question: {
          kind: 'choice',
          text: 'Canandaigua means, in the Seneca language, roughly',
          options: ['The chosen spot', 'The long lake', 'The place of salt', 'The falling water'],
          answer: 0,
          explain: 'The chosen spot, or the chosen place. The Seneca had a village here before the Revolution.',
        },
        postcard: 'Susan B. Anthony was tried in the courthouse here in 1873 for the crime of voting.',
      },
      {
        place: 'leroy',
        question: {
          kind: 'choice',
          text: 'What was invented in Le Roy in 1897?',
          options: ['Jell-O', 'The potato chip', 'Chewing gum', 'The teddy bear'],
          answer: 0,
          explain: 'A carpenter named Pearle Wait mixed it up in his kitchen. The plant made it here until 1964.',
        },
        postcard: 'The Jell-O Gallery museum keeps the story, and the old plant road is still called Jell-O Road.',
      },
    ],
  },

  // -------------------------------------------------------------------------
  {
    id: 'erie-canal-1845',
    title: 'Low bridge: the Erie Canal',
    era: 'canal',
    year: 1845,
    region: 'across',
    intro:
      'It is 1845 and you are aboard a packet boat, towed along at four miles an hour by a pair of mules. Albany to Buffalo takes the better part of a week. You eat aboard, you sleep aboard, and you duck for the bridges.',
    stops: [
      {
        place: 'cohoes',
        question: {
          kind: 'choice',
          text: 'What made Cohoes a mill town?',
          options: ['The falls of the Mohawk, power for the mills', 'Salt', 'Iron ore', 'A railroad shop'],
          answer: 0,
          explain: 'The Mohawk drops seventy feet at Cohoes, just before it meets the Hudson.',
        },
        postcard: 'The canal climbed past the falls through a staircase of locks. The Harmony Mills came later and were among the largest cotton mills in the country.',
      },
      {
        place: 'littlefalls',
        question: {
          kind: 'choice',
          text: 'Why did the canal builders need locks at Little Falls?',
          options: ['The Mohawk drops through a gorge here', 'It was the halfway point', 'Salt was mined here', 'It was the county seat'],
          answer: 0,
          explain: 'The river falls forty feet through the gorge, and the boats had to be lifted around it.',
        },
        postcard: 'Lock 17 at Little Falls, on the 1918 Barge Canal, has one of the tallest lifts on the whole system, about forty feet.',
      },
      {
        place: 'rome',
        question: {
          kind: 'choice',
          text: 'Ground was broken for the Erie Canal at Rome on',
          options: ['July 4, 1817', 'July 4, 1825', 'January 1, 1800', 'October 26, 1825'],
          answer: 0,
          explain: 'They started in the middle, where the digging was flat. The canal opened end to end on 26 October 1825.',
        },
        postcard: 'Fort Stanwix has been rebuilt on its original site, right in the middle of downtown.',
      },
      {
        place: 'chittenango',
        question: {
          kind: 'choice',
          text: 'Chittenango is the birthplace of',
          options: ['L. Frank Baum, who wrote The Wizard of Oz', 'Mark Twain', 'Grover Cleveland', 'James Fenimore Cooper'],
          answer: 0,
          explain: 'Baum was born here in 1856. The sidewalks downtown are painted yellow brick.',
        },
        postcard: 'The canal\'s dry dock at Chittenango is a museum now, with a boat under repair the way it would have been.',
      },
      {
        place: 'montezuma',
        question: {
          kind: 'choice',
          text: 'The canal crossed a great marsh at Montezuma. What laid the diggers low there?',
          options: ['Fever from the mosquitoes', 'The cold', 'Bandits', 'Quicksand'],
          answer: 0,
          explain: 'Malaria swept the camps in the summer of 1819, and the work stopped until the frost came.',
        },
        postcard: 'The marsh is the Montezuma National Wildlife Refuge now, and the geese come through by the thousand each fall.',
      },
      {
        place: 'palmyra',
        question: {
          kind: 'choice',
          text: 'The old song tells you "low bridge, everybody down" because',
          options: ['Packet boats passed under low farm bridges', 'The tunnels were narrow', 'The mules kicked', 'The locks were shallow'],
          answer: 0,
          explain: 'Farmers were owed a crossing wherever the canal cut their land, and the bridges were built low and cheap.',
        },
        postcard: 'Palmyra has four churches on one corner. The Smith family farm, where Joseph Smith grew up, is just south of the village.',
      },
      {
        place: 'lockport',
        question: {
          kind: 'choice',
          text: 'How many locks were in the famous flight that lifted boats up the escarpment at Lockport?',
          options: ['Five', 'Two', 'Ten', 'Twelve'],
          answer: 0,
          explain: 'Two flights of five, side by side, one for boats going up and one for boats coming down.',
        },
        postcard: 'The locks gave the city its name. A modern pair replaced one flight in the 1910s; the other is still there to look at.',
      },
    ],
  },

  // -------------------------------------------------------------------------
  {
    id: 'catskill-reservoirs-1955',
    title: 'Reservoirs of the Catskills',
    era: 'thruway',
    year: 1955,
    region: 'catskills',
    intro:
      'It is 1955. New York City has been damming Catskill valleys for forty years to fill its taps, and the Pepacton is filling now. Whole hamlets have been cleared, church by church. This trip goes looking for them.',
    stops: [
      {
        place: 'oldgilboa',
        clue: 'The original village of Gilboa, under the Schoharie Reservoir since 1926.',
        question: {
          kind: 'choice',
          text: 'What was found in the rock when the dam at Gilboa was dug in the 1920s?',
          options: ['Fossil tree stumps from the oldest known forest', 'Gold', 'Dinosaur bones', 'A Dutch fort'],
          answer: 0,
          explain: 'The Gilboa fossils are the stumps of trees that grew here some 380 million years ago.',
        },
        postcard: 'The old village lies under the reservoir. Gilboa was rebuilt above it.',
      },
      {
        place: 'olivecity',
        clue: 'A hamlet in the Esopus valley, cleared for the Ashokan Reservoir along with about a dozen others.',
        question: {
          kind: 'choice',
          text: 'Whose drinking water does the Ashokan Reservoir hold?',
          options: ['New York City\'s', 'Kingston\'s', 'Albany\'s', 'Poughkeepsie\'s'],
          answer: 0,
          explain: 'The city built the Ashokan between 1907 and 1915, and the Catskill Aqueduct carries the water ninety-odd miles south.',
        },
        postcard: 'About two thousand people were moved out of the valley to make room for it.',
      },
      {
        place: 'westshokan',
        question: {
          kind: 'truefalse',
          text: 'West Shokan was rebuilt on higher ground after the reservoir took the original.',
          answer: true,
          explain: 'Several of the Ashokan hamlets moved uphill and kept their names.',
        },
        postcard: 'The road along the dividing weir, between the two basins, is one of the finest drives in the Catskills.',
      },
      {
        place: 'arena',
        clue: 'The hamlet at the upper end of the Pepacton, on the East Branch of the Delaware, that went under in 1955.',
        question: {
          kind: 'choice',
          text: 'Which river was dammed to make the Pepacton Reservoir?',
          options: ['The East Branch of the Delaware', 'The Esopus', 'The Schoharie', 'The Neversink'],
          answer: 0,
          explain: 'The dam at Downsville holds back the East Branch for eighteen miles.',
        },
        postcard: 'Four hamlets went under: Arena, Pepacton, Shavertown and Union Grove.',
      },
      {
        place: 'shavertown',
        clue: 'A Pepacton hamlet, with a covered bridge, lost in 1955.',
        question: {
          kind: 'truefalse',
          text: 'The Pepacton holds more water than any other New York City reservoir.',
          answer: true,
          explain: 'It is the biggest of them by volume, and on its own it supplies about a quarter of the city\'s water.',
        },
        postcard: 'When the water is low in a dry year, you can still make out the old road and the bridge abutments.',
      },
      {
        place: 'downsville',
        question: {
          kind: 'choice',
          text: 'Downsville sits just below',
          options: ['The Pepacton dam', 'The Cannonsville dam', 'The Ashokan dam', 'The Gilboa dam'],
          answer: 0,
          explain: 'The dam is at the west end of the reservoir, right above the village.',
        },
        postcard: 'The Downsville covered bridge of 1854 still carries cars across the East Branch.',
      },
      {
        place: 'lackawack',
        clue: 'One of three hamlets under the Rondout Reservoir since 1950.',
        question: {
          kind: 'choice',
          text: 'Rondout water reaches the city through which tunnel, the longest in the world when it opened in 1944?',
          options: ['The Delaware Aqueduct', 'The Catskill Aqueduct', 'The Croton Aqueduct', 'The Holland Tunnel'],
          answer: 0,
          explain: 'About 85 miles of tunnel, and still the longest in the world.',
        },
        postcard: 'The Neversink hamlets went under in 1953. Cannonsville, Rock Rift and Rock Royal followed in the 1960s.',
      },
    ],
  },

  // -------------------------------------------------------------------------
  {
    id: 'hudson-sloop-1800',
    title: 'Up the Hudson by sloop',
    era: 'sloop',
    year: 1800,
    region: 'hudson',
    intro:
      'It is 1800. The road north is a rutted track, so anyone with the fare goes by sloop. New York to Albany takes three or four days if the wind and the tide are with you, and a week if they are not.',
    stops: [
      {
        place: 'tarrytown',
        question: {
          kind: 'choice',
          text: 'Washington Irving\'s home, Sunnyside, is at Tarrytown. Which of his stories is set next door?',
          options: ['The Legend of Sleepy Hollow', 'Rip Van Winkle', 'Moby-Dick', 'The Deerslayer'],
          answer: 0,
          explain: 'Sleepy Hollow is the next village north. Rip Van Winkle sleeps in the Catskills.',
        },
        postcard: 'The Tappan Zee is the widest stretch of the river. The bridge across it came in 1955.',
      },
      {
        place: 'westpoint',
        question: {
          kind: 'estimate',
          text: 'The Military Academy at West Point opened in what year?',
          unit: 'years',
          min: 1770,
          max: 1850,
          step: 1,
          answer: 1802,
          tolerance: 10,
          explain: '1802, under President Jefferson. The fort had been there since the Revolution.',
        },
        postcard: 'In 1778 a great iron chain was stretched across the river here to keep British ships from passing.',
      },
      {
        place: 'newburgh',
        question: {
          kind: 'choice',
          text: 'George Washington kept his headquarters at Newburgh',
          options: [
            'For the last year and a half of the Revolution',
            'During the winter at Valley Forge',
            'Never',
            'During the War of 1812',
          ],
          answer: 0,
          explain: 'The Hasbrouck House, 1782 to 1783. In 1850 it became the first historic site the state ever bought.',
        },
        postcard: 'The Newburgh to Beacon ferry ran until the bridge opened in 1963.',
      },
      {
        place: 'poughkeepsie',
        question: {
          kind: 'choice',
          text: 'How do you say Poughkeepsie?',
          options: ['puh-KIP-see', 'POE-kip-see', 'puff-KEEP-see', 'poe-KEEP-see'],
          answer: 0,
          explain: 'Puh-KIP-see. The name is Dutch spelling of a Wappinger word.',
        },
        postcard: 'Vassar College opened in 1861. The railroad bridge of 1889 is a walkway across the river now.',
      },
      {
        place: 'kingston',
        question: {
          kind: 'choice',
          text: 'Kingston was',
          options: [
            'The first capital of New York State, burned by the British in 1777',
            'A Dutch fort that never grew',
            'The birthplace of the Erie Canal',
            'A whaling port',
          ],
          answer: 0,
          explain: 'The first state senate met here in 1777. The British burned the town that October.',
        },
        postcard: 'The Senate House still stands in the Stockade district.',
      },
      {
        place: 'catskill',
        question: {
          kind: 'choice',
          text: 'Which painter made his home at Catskill and started the Hudson River School?',
          options: ['Thomas Cole', 'Frederic Church', 'Winslow Homer', 'Norman Rockwell'],
          answer: 0,
          explain: 'Cole came in 1825. Frederic Church, his student, built Olana on the hill across the river.',
        },
        postcard: 'In 1800 you crossed to Hudson by ferry. The Rip Van Winkle Bridge came in 1935.',
      },
      {
        place: 'hudson',
        question: {
          kind: 'choice',
          text: 'Hudson was founded in 1785 by',
          options: ['Whalers from Nantucket', 'Dutch fur traders', 'A railroad', 'A canal company'],
          answer: 0,
          explain: 'They wanted a harbour the British navy could not reach, and found one 120 miles from the sea.',
        },
        postcard: 'For a few decades Hudson was a whaling port. The ships sailed down the river and out to the Pacific.',
      },
    ],
  },

  // -------------------------------------------------------------------------
  {
    id: 'say-it-like-a-local',
    title: 'Say it like a local',
    era: 'modern',
    year: 2000,
    region: 'state',
    intro:
      'Every one of these towns has a name that trips up the out-of-towner. Find the town on the map, then pick the way the people who live there say it.',
    stops: [
      {
        place: 'valatie',
        question: {
          kind: 'choice',
          text: 'Valatie is said',
          options: ['va-LAY-sha', 'va-LAT-ee', 'VAL-uh-tie', 'va-LAH-tee'],
          answer: 0,
          explain: 'Va-LAY-sha. It is Dutch for little falls.',
        },
        postcard: 'Kinderhook, next door, gave the country President Van Buren.',
      },
      {
        place: 'coxsackie',
        question: {
          kind: 'choice',
          text: 'Coxsackie is said',
          options: ['cook-SOCK-ee', 'COX-sack-ee', 'cox-SAY-kee', 'COOK-sah-kee'],
          answer: 0,
          explain: 'Cook-SOCK-ee. The name is older than the Dutch.',
        },
        postcard: 'The coxsackievirus is named for the village, where the first samples were taken in 1948.',
      },
      {
        place: 'cairo',
        question: {
          kind: 'choice',
          text: 'Cairo, in Greene County, is said',
          options: ['KAY-ro', 'KY-ro', 'CAR-o', 'KAY-ee-ro'],
          answer: 0,
          explain: 'KAY-ro, like the syrup. Nobody in Greene County says it like the city in Egypt.',
        },
        postcard: 'Cairo was the doorway to the Catskill resorts. The boarding houses went up the mountain from here.',
      },
      {
        place: 'canajoharie',
        question: {
          kind: 'choice',
          text: 'Canajoharie is said',
          options: ['can-uh-juh-HAIR-ee', 'CAN-uh-jo-harry', 'can-AJ-oh-hah-ree', 'kah-nah-JAR-ee'],
          answer: 0,
          explain: 'Can-uh-juh-HAIR-ee. Locals often just say Canajo.',
        },
        postcard: 'The name is Mohawk for the pot that washes itself, after a pothole in the creek. Beech-Nut made its baby food here for a century.',
      },
      {
        place: 'chili',
        question: {
          kind: 'choice',
          text: 'Chili, outside Rochester, is said',
          options: ['CHY-lye', 'CHILL-ee', 'CHEE-lee', 'shy-LEE'],
          answer: 0,
          explain: 'CHY-lye, rhymes with my eye. It was named for the country, and then everyone forgot how the country is said.',
        },
        postcard: 'Western New York is full of borrowed names: Greece, Mexico, Peru and Cuba are all a short drive away.',
      },
      {
        place: 'nunda',
        question: {
          kind: 'choice',
          text: 'Nunda is said',
          options: ['nun-DAY', 'NUN-duh', 'NOON-dah', 'nun-DAH'],
          answer: 0,
          explain: 'Nun-DAY. It comes from a Seneca word for hilly.',
        },
        postcard: 'Letchworth State Park, the Grand Canyon of the East, is just up the road.',
      },
      {
        place: 'schaghticoke',
        question: {
          kind: 'choice',
          text: 'Schaghticoke is said',
          options: ['SKAT-i-coke', 'SHAG-ti-coke', 'skag-TIE-coke', 'SHACK-tee-cook'],
          answer: 0,
          explain: 'SKAT-i-coke. The spelling has beaten generations of newcomers.',
        },
        postcard: 'The Schaghticoke Fair has run every year since 1819, one of the oldest in the state.',
      },
      {
        place: 'copake',
        question: {
          kind: 'choice',
          text: 'Copake is said',
          options: ['co-PAKE', 'COPE-ake', 'KOP-uh-kee', 'co-PAH-kay'],
          answer: 0,
          explain: 'Co-PAKE, rhymes with the lake.',
        },
        postcard: 'Bash Bish Falls is a short walk over the Massachusetts line from Copake Falls.',
      },
    ],
  },

  // -------------------------------------------------------------------------
  {
    id: 'northway-opens-1967',
    title: 'The Northway opens',
    era: 'northway',
    year: 1967,
    region: 'northroad',
    intro:
      'It is 1967 and the last stretch of the Northway has just opened through the mountains. For the first time you can drive from Albany to the border without a stoplight. Route 9, through the villages, goes quiet.',
    stops: [
      {
        place: 'cliftonpark',
        question: {
          kind: 'choice',
          text: 'Before the Northway, Clifton Park was',
          options: ['Mostly farms', 'A city', 'A resort', 'A mill town'],
          answer: 0,
          explain: 'Exit 9 came, and the subdivisions followed it out of the fields.',
        },
        postcard: 'The Thruway and the Northway meet at Exit 24, the busiest interchange north of the city.',
      },
      {
        place: 'lakegeorge',
        question: {
          kind: 'choice',
          text: 'The Northway reached Lake George in the early 1960s. What did that do to Route 9 through the village?',
          options: ['Through traffic thinned out overnight', 'Nothing changed', 'It closed', 'It became one-way'],
          answer: 0,
          explain: 'Cars bound for the north took the highway. The village became a place you chose to stop, not one you had to drive through.',
        },
        postcard: 'The Million Dollar Beach opened at the south end of the lake in 1951.',
      },
      {
        place: 'schroonlake',
        question: {
          kind: 'estimate',
          text: 'Albany to Montreal on Route 9 took about half a day. On the Northway, about how many hours?',
          unit: 'hours',
          min: 1,
          max: 8,
          step: 0.5,
          answer: 3.5,
          tolerance: 0.75,
          explain: 'Under four hours, border stop included.',
        },
        postcard: 'Schroon Lake\'s exit brought weekenders from Albany who used to think it was too far.',
      },
      {
        place: 'northhudson',
        question: {
          kind: 'choice',
          text: 'Frontier Town, the wild-west park by the North Hudson exit, opened in 1952 and closed in',
          options: ['1998', '1967', '1975', '2010'],
          answer: 0,
          explain: 'It hung on for nearly fifty years. The state reopened the site as a campground in the late 2010s.',
        },
        postcard: 'Blue Ridge Road runs west from here to Newcomb and the old Tahawus road.',
      },
      {
        place: 'keenevalley',
        question: {
          kind: 'choice',
          text: 'The last section of the Northway to open, in 1967, ran',
          options: [
            'Through the mountains between Pottersville and Keene Valley',
            'From Albany to Saratoga',
            'From Plattsburgh to Canada',
            'Around Glens Falls',
          ],
          answer: 0,
          explain: 'The hardest miles were the ones through the High Peaks, and they came last.',
        },
        postcard: 'Parade magazine named the Northway the most beautiful highway in America in 1966, before it was even finished.',
      },
      {
        place: 'peru',
        question: {
          kind: 'truefalse',
          text: 'In 1967 the Northway ran past an Air Force base at Plattsburgh.',
          answer: true,
          explain: 'Plattsburgh Air Force Base, 1955 to 1995. The bombers flew over the lake.',
        },
        postcard: 'Peru\'s apple orchards run down to the Champlain shore.',
      },
    ],
  },

  // -------------------------------------------------------------------------
  {
    id: 'southern-tier-1960',
    title: 'Route 17 along the Southern Tier',
    era: 'thruway',
    year: 1960,
    region: 'southerntier',
    intro:
      'It is 1960 and the Quickway is opening in pieces, so Route 17 is a highway one mile and a village main street the next. It runs the whole bottom of the state, from the Catskill resorts to the Pennsylvania line.',
    stops: [
      {
        place: 'monticello',
        question: {
          kind: 'choice',
          text: 'Sullivan County\'s summer resorts were known as',
          options: ['The Borscht Belt', 'The Gold Coast', 'Cottage Country', 'The Riviera'],
          answer: 0,
          explain: 'Grossinger\'s at Liberty and the Concord at Kiamesha Lake were the big ones.',
        },
        postcard: 'Monticello Raceway opened in 1958, harness racing under the lights.',
      },
      {
        place: 'bethel',
        question: {
          kind: 'choice',
          text: 'What happened on Max Yasgur\'s dairy farm near Bethel in August 1969?',
          options: ['The Woodstock festival', 'A plane crash', 'A gold strike', 'The state fair'],
          answer: 0,
          explain: 'Half a million people in a hayfield. The town of Woodstock itself is sixty miles away in Ulster County.',
        },
        postcard: 'The field is a museum and a concert lawn now.',
      },
      {
        place: 'roscoe',
        question: {
          kind: 'choice',
          text: 'Roscoe calls itself',
          options: ['Trout Town USA', 'The Glass City', 'Hometown USA', 'The Salt City'],
          answer: 0,
          explain: 'Fly fishing was more or less invented in America on the Beaverkill and the Willowemoc.',
        },
        postcard: 'Junction Pool, where the two rivers meet, is the most famous spot on either.',
      },
      {
        place: 'hancock',
        question: {
          kind: 'choice',
          text: 'At Hancock, two branches join to form',
          options: ['The Delaware River', 'The Susquehanna', 'The Hudson', 'The Mohawk'],
          answer: 0,
          explain: 'The East Branch and the West Branch meet here, and the Delaware runs on as the state line.',
        },
        postcard: 'Up the West Branch, Cannonsville was being cleared for its reservoir as this trip was made.',
      },
      {
        place: 'elmira',
        question: {
          kind: 'choice',
          text: 'Which writer spent his summers at Elmira and is buried there?',
          options: ['Mark Twain', 'Herman Melville', 'Walt Whitman', 'James Fenimore Cooper'],
          answer: 0,
          explain: 'He wrote much of Tom Sawyer and Huckleberry Finn in an octagonal study at Quarry Farm.',
        },
        postcard: 'The study now sits on the Elmira College campus.',
      },
      {
        place: 'corning',
        question: {
          kind: 'choice',
          text: 'Corning is known for',
          options: ['Glass', 'Steel', 'Shoes', 'Carpets'],
          answer: 0,
          explain: 'Corning Glass Works came up the river from Brooklyn in 1868.',
        },
        postcard: 'The 200-inch mirror for the Palomar telescope was cast here in 1934.',
      },
      {
        place: 'salamanca',
        question: {
          kind: 'truefalse',
          text: 'Salamanca is built almost entirely on land leased from the Seneca Nation.',
          answer: true,
          explain: 'It sits inside the Allegany Reservation. The old 99-year leases ran out in 1991.',
        },
        postcard: 'The Erie Railroad shops here were the reason the city grew.',
      },
    ],
  },
];
